import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogBodyProps } from '@fluentui/react-dialog'
import useAxios from '../../../../hooks/useAxios'
import {
    Button,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogTrigger,
    Divider,
    InteractionTag,
    InteractionTagPrimary,
    mergeClasses,
    Text,
} from '@fluentui/react-components'
import { HammockDialogProps } from '../HammockDialog'
import { Destination } from '../../../../context/AxiosContext'
import Loading from '../../../../components/Loading'
import { ExtendedEmailOverviewDto } from '../../../pages/File/ViewFileTabs/FileEmailOverview'
import { Attachment, Message, Recipient } from '@microsoft/microsoft-graph-types'
// @ts-expect-error("dompurify exports differ from what ts-lint is expecting and cannot see the default export")
import DOMPurify from 'dompurify'
import FormattedDateTime from '../../../../components/Date/FormattedDateTime'
import { toastContainerId, useApplicationStyles } from '../../../../pages/App'
import { toast } from 'react-toastify'
import { DocumentOpenType } from '../../../../context/GlobalContext'
import { stealthClick } from '../../../../components/StealthLink'
import { bundleIcon, OpenFilled, OpenRegular } from '@fluentui/react-icons'
import { isOfficeInitialized } from '../../../../helpers/outlookHelper'
import { useSettings } from '../../../../hooks/useSettings'

const OpenIcon = bundleIcon(OpenFilled, OpenRegular)

export type PreviewMessageDialogBodyProps = DialogBodyProps & {
    message: ExtendedEmailOverviewDto
    destination: Destination
    userEmail: string
}

export default function PreviewMessageDialogBody(props: HammockDialogProps<PreviewMessageDialogBodyProps>) {
    const axios = useAxios()
    const appStyles = useApplicationStyles()
    const { settings } = useSettings()
    const { t: translate } = useTranslation()

    const dialogBodyProps = useMemo(() => props.dialogBodyProps?.() ?? ({} as PreviewMessageDialogBodyProps), [props])

    const [fullMessage, setFullMessage] = useState<Message>(undefined)

    const isHtmlBody = fullMessage?.body?.contentType?.toLowerCase() === 'html'
    const sanitizedHtmlBody = useMemo(() => {
        let result =
            isHtmlBody && fullMessage?.body?.content
                ? DOMPurify.sanitize(fullMessage.body.content)
                : (fullMessage?.bodyPreview ?? dialogBodyProps.message?.bodyPreview)

        fullMessage?.attachments
            ?.filter(attachment => attachment['contentId'])
            .forEach((attachment: Attachment) => {
                const contentId = `cid:${attachment['contentId']}`
                const startIndex = result.indexOf(contentId)
                if (startIndex >= 0) {
                    result = result.replaceAll(
                        `cid:${attachment['contentId']}`,
                        `data:${attachment.contentType};base64, ${attachment['contentBytes']}`,
                    )
                }
            })

        return result
    }, [isHtmlBody, fullMessage])
    const documentOpenType: DocumentOpenType = useMemo(() => {
        if (!isOfficeInitialized()) {
            return DocumentOpenType.Download
        }

        return settings.documentsOpen ?? DocumentOpenType.Open
    }, [])

    useEffect(() => {
        if (!dialogBodyProps.message) {
            setFullMessage(undefined)
            return
        }
        axios
            .get(`/messages/${dialogBodyProps.message.id}`, {
                baseURL: dialogBodyProps.destination,
                headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId", outlook.body-content-type="html"' },
                params: {
                    $select:
                        'id,internetMessageId,conversationId,subject,bodyPreview,body,attachments,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                    $expand: 'attachments',
                },
            })
            .then(response => setFullMessage(response.data))
    }, [dialogBodyProps.message])

    if (!dialogBodyProps.message) {
        return <></>
    }

    return (
        <DialogBody className={appStyles.overflowXHidden}>
            <DialogContent className={appStyles.overflowXHidden}>
                <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS, appStyles.flexColumn)}>
                    <Text weight={'semibold'}>{dialogBodyProps.message.subject ?? translate('file.pivot.email.preview.button')}</Text>
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS, appStyles.flexColumn)}>
                        <div className={mergeClasses(appStyles.displayFlex, appStyles.spaceBetween)}>
                            <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                                {translate('file.pivot.email.preview.from')}:{' '}
                                <InteractionTag
                                    shape="rounded"
                                    size="small"
                                    onClick={() => {
                                        navigator.clipboard.writeText(`${dialogBodyProps.message.sender?.emailAddress}`)
                                        toast(translate('file.pivot.email.preview.copied'), { autoClose: 1000, containerId: toastContainerId })
                                    }}
                                >
                                    <InteractionTagPrimary>
                                        {dialogBodyProps.message.sender?.emailAddress?.name || translate('base.unknown')}
                                    </InteractionTagPrimary>
                                </InteractionTag>
                            </div>
                            <div>{dialogBodyProps.message.sentDateTime && <FormattedDateTime date={dialogBodyProps.message.sentDateTime} />}</div>
                        </div>
                        <div>
                            {translate('file.pivot.email.preview.to')}:{' '}
                            {dialogBodyProps.message.toRecipients?.map((recipient: Recipient, index: number) => (
                                <InteractionTag
                                    shape="rounded"
                                    key={index}
                                    size="small"
                                    onClick={() => {
                                        navigator.clipboard.writeText(`${recipient?.emailAddress}`)
                                        toast(translate('file.pivot.email.preview.copied'), { autoClose: 1000, containerId: toastContainerId })
                                    }}
                                >
                                    <InteractionTagPrimary>{recipient?.emailAddress?.name || translate('base.unknown')}</InteractionTagPrimary>
                                </InteractionTag>
                            ))}
                        </div>
                        {fullMessage?.hasAttachments && (
                            <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS, appStyles.flexWrap)}>
                                {translate('file.pivot.email.preview.attachments')}:{' '}
                                {fullMessage?.attachments?.map((attachment: Attachment, index: number) => (
                                    <InteractionTag shape="rounded" key={index} size="small">
                                        <InteractionTagPrimary>{attachment?.name || translate('base.unknown')}</InteractionTagPrimary>
                                    </InteractionTag>
                                ))}
                            </div>
                        )}
                    </div>
                    <Divider />
                    {!fullMessage && <Loading />}
                    {fullMessage && isHtmlBody && (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: sanitizedHtmlBody,
                            }}
                        ></div>
                    )}
                    {fullMessage && !isHtmlBody && <div style={{ whiteSpace: 'pre-wrap' }}>{fullMessage?.body?.content || ''}</div>}
                </div>
            </DialogContent>
            <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                    <Button
                        appearance="subtle"
                        icon={<OpenIcon />}
                        onClick={e => {
                            e.preventDefault()
                            stealthClick(
                                {
                                    id: dialogBodyProps.message.id,
                                    universalId: dialogBodyProps.message.internetMessageId,
                                    destination: Destination.ReplicationEmail,
                                    url: dialogBodyProps.message.url,
                                    documentsOpen: documentOpenType,
                                },
                                axios,
                            )
                        }}
                    >
                        {translate('action.open')}
                    </Button>
                </DialogTrigger>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{translate('action.close')}</Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
